import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { taxonomiesApi } from '../api/taxonomiesApi'
import type { Taxonomy, TaxonomyTerm } from '../model/taxonomy'

export interface TaxonomiesState {
  taxonomies: Taxonomy[]
  selectedTaxonomyId: number | null
  selectedTermPath: number[]
  termColumns: Record<string, TaxonomyTerm[]>
  loadingTaxonomies: boolean
  treeLoaded: boolean
  error: string | null
}

const initialState: TaxonomiesState = {
  taxonomies: [],
  selectedTaxonomyId: null,
  selectedTermPath: [],
  termColumns: {},
  loadingTaxonomies: false,
  treeLoaded: false,
  error: null,
}

export function termColumnKey(taxonomyId: number, parentId: number | null) {
  return `${taxonomyId}:${parentId ?? 'root'}`
}

export const fetchTaxonomies = createAsyncThunk(
  'catalogTaxonomies/fetchTaxonomies',
  async (options?: { force?: boolean }) => taxonomiesApi.getTree(),
  {
    condition: (options: { force?: boolean } | undefined, { getState }) => {
      const state = (getState() as { catalogTaxonomies?: TaxonomiesState }).catalogTaxonomies
      return Boolean(options?.force) || (!state?.treeLoaded && !state?.loadingTaxonomies)
    },
  },
)

const taxonomiesSlice = createSlice({
  name: 'catalogTaxonomies',
  initialState,
  reducers: {
    selectTaxonomy(state, action: PayloadAction<number | null>) {
      state.selectedTaxonomyId = action.payload
      state.selectedTermPath = []
    },
    selectTerm(state, action: PayloadAction<{ depth: number; termId: number }>) {
      state.selectedTermPath = state.selectedTermPath.slice(0, action.payload.depth)
      state.selectedTermPath[action.payload.depth] = action.payload.termId
    },
    clearTermPath(state, action: PayloadAction<number>) {
      state.selectedTermPath = state.selectedTermPath.slice(0, action.payload)
    },
    taxonomySaved(state, action: PayloadAction<Taxonomy>) {
      const index = state.taxonomies.findIndex((item) => item.id === action.payload.id)
      if (index === -1) state.taxonomies.push(action.payload)
      else state.taxonomies[index] = action.payload
      state.taxonomies.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name))
      state.selectedTaxonomyId = action.payload.id
      state.selectedTermPath = []
      state.termColumns[termColumnKey(action.payload.id, null)] ??= []
    },
    taxonomyRemoved(state, action: PayloadAction<number>) {
      state.taxonomies = state.taxonomies.filter((item) => item.id !== action.payload)
      if (state.selectedTaxonomyId === action.payload) {
        state.selectedTaxonomyId = null
        state.selectedTermPath = []
      }
      for (const key of Object.keys(state.termColumns)) {
        if (key.startsWith(`${action.payload}:`)) delete state.termColumns[key]
      }
    },
    termSaved(state, action: PayloadAction<{ key: string; term: TaxonomyTerm }>) {
      for (const [key, items] of Object.entries(state.termColumns)) {
        state.termColumns[key] = items.filter((item) => item.id !== action.payload.term.id)
      }
      const terms = state.termColumns[action.payload.key] ?? []
      const index = terms.findIndex((item) => item.id === action.payload.term.id)
      if (index === -1) terms.push(action.payload.term)
      else terms[index] = action.payload.term
      terms.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name))
      state.termColumns[action.payload.key] = terms
    },
    termRemoved(state, action: PayloadAction<{ key: string; termId: number }>) {
      state.termColumns[action.payload.key] = (state.termColumns[action.payload.key] ?? [])
        .filter((term) => term.id !== action.payload.termId)
      for (const key of Object.keys(state.termColumns)) {
        state.termColumns[key] = state.termColumns[key].filter((term) => term.parent_id !== action.payload.termId)
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchTaxonomies.pending, (state) => {
        state.loadingTaxonomies = true
        state.error = null
      })
      .addCase(fetchTaxonomies.fulfilled, (state, action) => {
        state.loadingTaxonomies = false
        state.treeLoaded = true
        state.taxonomies = action.payload.taxonomies
        state.termColumns = {}
        for (const term of action.payload.terms) {
          const key = termColumnKey(term.taxonomy_id, term.parent_id)
          ;(state.termColumns[key] ??= []).push(term)
        }
        for (const terms of Object.values(state.termColumns)) {
          terms.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name) || a.id - b.id)
        }
        if (!state.taxonomies.some((item) => item.id === state.selectedTaxonomyId)) {
          state.selectedTaxonomyId = state.taxonomies[0]?.id ?? null
          state.selectedTermPath = []
        }
      })
      .addCase(fetchTaxonomies.rejected, (state, action) => {
        state.loadingTaxonomies = false
        state.error = action.error.message ?? 'Không tải được danh sách phân loại.'
      })
  },
})

export const taxonomiesActions = taxonomiesSlice.actions
export const taxonomiesReducer = taxonomiesSlice.reducer
